import {
  Injectable,
  inject,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import {
  Database,
  ref,
  objectVal,
  set,
  remove,
  serverTimestamp,
} from "@angular/fire/database";

export interface BountyDraw {
  value: number;
  killerID: string | null;
  playerID: string | null; // the eliminated (knocked-out) player
}

export interface ClockOverlay {
  type: "seatDraw" | "tableBreak" | "tableBalance" | "mysteryBounty";
  timestamp: number;
  playerMoves?: any[];
  tableBreaking?: number | string;
  // Mystery bounty reveal — one or more draws played as a single ceremony.
  draws?: BountyDraw[];
  order?: number[];
}

@Injectable({
  providedIn: "root",
})
export class ClockOverlayService {
  private readonly mdb = inject(Database);
  private readonly injector = inject(EnvironmentInjector);
  private activeOverlay$ = new BehaviorSubject<ClockOverlay | null>(null);
  private overlayPath!: string | null;
  private overlaySub!: Subscription | null;
  private currentTournID!: string | null;

  get overlay(): Observable<ClockOverlay | null> {
    return this.activeOverlay$.asObservable();
  }

  get currentOverlay(): ClockOverlay | null {
    return this.activeOverlay$.value;
  }

  private write(path: string, data: any) {
    return runInInjectionContext(this.injector, () =>
      set(ref(this.mdb, path), data)
    );
  }

  init(tournID: string): void {
    this.cleanup();
    this.currentTournID = tournID;
    this.overlayPath = "tournaments/" + tournID + "/clockOverlay";

    // Subscribe to the Firebase overlay node.
    this.overlaySub = runInInjectionContext(this.injector, () =>
      objectVal<any>(ref(this.mdb, this.overlayPath!))
    ).subscribe((overlay: any) => {
      this.activeOverlay$.next(overlay || null);
    });
  }

  showSeatDraw(): void {
    if (!this.overlayPath) return;
    this.write(this.overlayPath, {
      type: "seatDraw",
      timestamp: serverTimestamp(),
    });
  }

  showTableBreak(tableBreaking: number | string): void {
    if (!this.overlayPath) return;
    this.write(this.overlayPath, {
      type: "tableBreak",
      timestamp: serverTimestamp(),
      playerMoves: [],
      tableBreaking: tableBreaking,
    });
  }

  showTableBalance(): void {
    if (!this.overlayPath) return;
    this.write(this.overlayPath, {
      type: "tableBalance",
      timestamp: serverTimestamp(),
      playerMoves: [],
    });
  }

  updatePlayerMoves(playerMoves: any[]): void {
    if (!this.overlayPath) return;
    this.write(this.overlayPath + "/playerMoves", playerMoves);
  }

  // Mystery bounty reveal — same mechanism as seat draw / table break. Written
  // by the device that drew; fullscreen clocks render it and the initiating
  // device opens a local dialog. `timestamp` (server clock) anchors the synced
  // reveal across all surfaces.
  showMysteryBounty(draws: BountyDraw[], order: number[]): void {
    if (!this.overlayPath) return;
    this.write(this.overlayPath, {
      type: "mysteryBounty",
      timestamp: serverTimestamp(),
      draws: (draws || []).map((d) => ({
        value: d.value,
        killerID: d.killerID || null,
        playerID: d.playerID || null,
      })),
      order: order || [],
    });
  }

  dismiss(): void {
    if (!this.overlayPath) return;
    runInInjectionContext(this.injector, () =>
      remove(ref(this.mdb, this.overlayPath!))
    );
  }

  cleanup(): void {
    if (this.overlaySub) {
      this.overlaySub.unsubscribe();
      this.overlaySub = null;
    }
    this.activeOverlay$.next(null);
    this.overlayPath = null;
    this.currentTournID = null;
  }
}
